import { useState } from "react";
import type {
  DiscoverySourceOption,
  LeaderboardFilters,
  SubScoreKey,
  TokenVerifiedOption,
  TrendOption,
} from "../types/filters.ts";
import {
  DISCOVERY_SOURCE_OPTIONS,
  FAMILY_OPTIONS,
  RAISE_BUCKET_OPTIONS,
  SCORE_BAND_OPTIONS,
  SUBSCORE_KEYS,
  TOKEN_VERIFIED_OPTIONS,
  TREND_OPTIONS,
} from "../types/filters.ts";

interface Props {
  filters: LeaderboardFilters;
  onChange: (patch: Partial<LeaderboardFilters>) => void;
}

function ChipMulti<T extends string>({
  options,
  selected,
  onToggle,
  labelFor,
}: {
  options: readonly T[];
  selected: T[] | undefined;
  onToggle: (next: T[]) => void;
  labelFor?: (value: T) => string;
}) {
  const set = new Set(selected ?? []);
  return (
    <div className="leaderboard-filter-chips">
      {options.map((opt) => {
        const isOn = set.has(opt);
        return (
          <button
            key={opt}
            type="button"
            className={`leaderboard-filter-chip${isOn ? " is-active" : ""}`}
            onClick={() => {
              const next = new Set(set);
              if (isOn) next.delete(opt);
              else next.add(opt);
              onToggle(Array.from(next));
            }}
          >
            {labelFor ? labelFor(opt) : opt}
          </button>
        );
      })}
    </div>
  );
}

function NumberRange({
  min,
  max,
  onChange,
  placeholder,
  step,
}: {
  min: number | undefined;
  max: number | undefined;
  onChange: (next: { min?: number; max?: number }) => void;
  placeholder?: { min: string; max: string };
  step?: number;
}) {
  return (
    <div className="leaderboard-filter-range">
      <input
        type="number"
        className="leaderboard-filter-num"
        value={min ?? ""}
        step={step}
        placeholder={placeholder?.min ?? "min"}
        onChange={(e) => {
          const v = e.target.value;
          onChange({ min: v === "" ? undefined : Number(v) });
        }}
      />
      <span className="leaderboard-filter-range-sep">–</span>
      <input
        type="number"
        className="leaderboard-filter-num"
        value={max ?? ""}
        step={step}
        placeholder={placeholder?.max ?? "max"}
        onChange={(e) => {
          const v = e.target.value;
          onChange({ max: v === "" ? undefined : Number(v) });
        }}
      />
    </div>
  );
}

function ToggleChip({
  label,
  active,
  onToggle,
}: {
  label: string;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className={`leaderboard-filter-chip${active ? " is-active" : ""}`}
      onClick={onToggle}
    >
      {label}
    </button>
  );
}

export function LeaderboardFilterPanel({ filters, onChange }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <div className="leaderboard-filter-panel">
      <button
        type="button"
        className="leaderboard-filter-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? "Hide filters" : "Advanced filters"}
        <span className="leaderboard-filter-toggle-chevron">{open ? "▴" : "▾"}</span>
      </button>

      {open && (
        <div className="leaderboard-filter-grid">
          <div className="leaderboard-filter-row">
            <span className="leaderboard-filter-label">Total score</span>
            <NumberRange
              min={filters.scoreMin}
              max={filters.scoreMax}
              placeholder={{ min: "0", max: "100" }}
              onChange={({ min, max }) => onChange({ scoreMin: min, scoreMax: max })}
            />
          </div>

          <div className="leaderboard-filter-row">
            <span className="leaderboard-filter-label">Score band</span>
            <ChipMulti
              options={SCORE_BAND_OPTIONS}
              selected={filters.scoreBand}
              onToggle={(v) => onChange({ scoreBand: v })}
            />
          </div>

          <div className="leaderboard-filter-row">
            <span className="leaderboard-filter-label">Family</span>
            <ChipMulti
              options={FAMILY_OPTIONS}
              selected={filters.family}
              onToggle={(v) => onChange({ family: v })}
            />
          </div>

          <div className="leaderboard-filter-row">
            <span className="leaderboard-filter-label">Funding raised (USD)</span>
            <NumberRange
              min={filters.raiseMin}
              max={filters.raiseMax}
              placeholder={{ min: "min", max: "max" }}
              step={1_000_000}
              onChange={({ min, max }) => onChange({ raiseMin: min, raiseMax: max })}
            />
          </div>

          <div className="leaderboard-filter-row">
            <span className="leaderboard-filter-label">Raise bucket</span>
            <ChipMulti
              options={RAISE_BUCKET_OPTIONS}
              selected={filters.raiseBucket}
              onToggle={(v) => onChange({ raiseBucket: v })}
            />
            <ToggleChip
              label="Stealth / undisclosed"
              active={Boolean(filters.raiseUnknown)}
              onToggle={() => onChange({ raiseUnknown: !filters.raiseUnknown })}
            />
          </div>

          <div className="leaderboard-filter-row">
            <span className="leaderboard-filter-label">Trend (7d)</span>
            <div className="leaderboard-filter-chips">
              {TREND_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={`leaderboard-filter-chip${filters.trend === opt ? " is-active" : ""}`}
                  onClick={() =>
                    onChange({
                      trend: filters.trend === opt ? undefined : (opt as TrendOption),
                    })
                  }
                >
                  {opt[0]?.toUpperCase() + opt.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="leaderboard-filter-row">
            <span className="leaderboard-filter-label">Sub-score</span>
            <select
              className="leaderboard-filter-select"
              value={filters.subscoreKey ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                onChange({
                  subscoreKey: val === "" ? undefined : (val as SubScoreKey),
                  subscoreMin: val === "" ? undefined : filters.subscoreMin,
                });
              }}
            >
              <option value="">— pick —</option>
              {SUBSCORE_KEYS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
            <input
              type="number"
              className="leaderboard-filter-num"
              value={filters.subscoreMin ?? ""}
              placeholder="min pts"
              disabled={!filters.subscoreKey}
              onChange={(e) => {
                const v = e.target.value;
                onChange({ subscoreMin: v === "" ? undefined : Number(v) });
              }}
            />
          </div>

          <div className="leaderboard-filter-row">
            <span className="leaderboard-filter-label">TGE confidence</span>
            <NumberRange
              min={filters.tgeConfidenceMin}
              max={filters.tgeConfidenceMax}
              placeholder={{ min: "0", max: "100" }}
              onChange={({ min, max }) =>
                onChange({ tgeConfidenceMin: min, tgeConfidenceMax: max })
              }
            />
            <ToggleChip
              label="Has TGE hints"
              active={Boolean(filters.hasTgeHints)}
              onToggle={() => onChange({ hasTgeHints: !filters.hasTgeHints })}
            />
          </div>

          <div className="leaderboard-filter-row">
            <span className="leaderboard-filter-label">Token state</span>
            {/* Tri-state chip pair: clicking "Has token" cycles undefined → true,
                clicking "No token" cycles undefined → false. Clicking the
                active one cycles back to undefined. */}
            <ToggleChip
              label="Has live token"
              active={filters.hasToken === true}
              onToggle={() => onChange({ hasToken: filters.hasToken === true ? undefined : true })}
            />
            <ToggleChip
              label="No live token"
              active={filters.hasToken === false}
              onToggle={() =>
                onChange({ hasToken: filters.hasToken === false ? undefined : false })
              }
            />
            <ChipMulti
              options={TOKEN_VERIFIED_OPTIONS}
              selected={filters.tokenVerified}
              onToggle={(v) => onChange({ tokenVerified: v as TokenVerifiedOption[] })}
              labelFor={(v) => v.toUpperCase()}
            />
          </div>

          <div className="leaderboard-filter-row">
            <span className="leaderboard-filter-label">Backers</span>
            <ToggleChip
              label="Has backer data"
              active={Boolean(filters.hasBackers)}
              onToggle={() => onChange({ hasBackers: !filters.hasBackers })}
            />
          </div>

          <div className="leaderboard-filter-row">
            <span className="leaderboard-filter-label">Audit freshness</span>
            <ToggleChip
              label="Within 7 days"
              active={filters.auditedWithinDays === 7}
              onToggle={() =>
                onChange({
                  auditedWithinDays: filters.auditedWithinDays === 7 ? undefined : 7,
                })
              }
            />
            <ToggleChip
              label="Older than 30 days"
              active={filters.auditedOlderThanDays === 30}
              onToggle={() =>
                onChange({
                  auditedOlderThanDays: filters.auditedOlderThanDays === 30 ? undefined : 30,
                })
              }
            />
          </div>

          <div className="leaderboard-filter-row">
            <span className="leaderboard-filter-label">Discovery source</span>
            <ChipMulti
              options={DISCOVERY_SOURCE_OPTIONS}
              selected={filters.discoverySource}
              onToggle={(v) => onChange({ discoverySource: v as DiscoverySourceOption[] })}
              labelFor={(v) => v.replace("_", " ")}
            />
          </div>

          <div className="leaderboard-filter-row">
            <span className="leaderboard-filter-label">Lifecycle</span>
            <ToggleChip
              label="Include archived"
              active={Boolean(filters.archived)}
              onToggle={() => onChange({ archived: !filters.archived })}
            />
          </div>
        </div>
      )}
    </div>
  );
}
